import { FC } from 'react';

import { PublicCapTablePayload } from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { Column, DetailsSortVariant, ViewBy } from '../../../types/snapshot.types';
import { snapshotViewBy } from '../../cap-table/desktop-details/snapshot-view-by/index';
import {
  getShareClasses,
  RenderableSnapshot,
} from '../../cap-table/desktop-details/snapshot-view-by/snapshot-view-by.types';
import { BasicTable } from '../../cap-table/desktop-details/table-variants/table-basic';

type PublicCapTableTableProps = {
  payload: PublicCapTablePayload;
  sortBy: DetailsSortVariant;
};

/**
 * The cap table's own renderer, reused unchanged: grouping by category, expandable sub-rows and
 * equity pool rows are all behaviour the shared page is meant to keep.
 */
const VIEW_BY: Record<ViewBy, boolean> = { stakeholders: true, type: true };

/**
 * The payload is narrower than `Snapshot` by design, so `RenderableSnapshot` names exactly what the
 * views read: a cast would let a renamed field on either side compile and fail at runtime.
 */
const toSnapshotLike = (payload: PublicCapTablePayload): RenderableSnapshot => ({
  dilutedShares: payload.dilutedShares,
  issuedShares: payload.issuedShares,
  stats: {
    pools: payload.pools,
    shareClasses: payload.shareClasses,
    sharePlans: [],
    stakeholders: payload.stakeholders,
    stakeholdersTypes: payload.stakeholdersTypes,
  },
});

export const PublicCapTableTable: FC<PublicCapTableTableProps> = ({ payload, sortBy }) => {
  const snapshot = toSnapshotLike(payload);
  // The row identity always ships — it is not an owner-selected column. The rest, including the
  // structural columns the backend adds, comes from the link's own selection.
  const visibleColumns = [...new Set([Column.NAME, ...payload.columns[sortBy]])];

  const { otherShareClasses } = getShareClasses(payload.defaultShareClassId, payload.shareClasses);
  const possibleShareClasses = otherShareClasses
    .filter(({ diluted }) => diluted)
    .map(({ name }) => name)
    .sort((left, right) => left.localeCompare(right));

  return (
    <BasicTable
      additionalColumns={{
        [Column.OTHER_SHARE_CLASSES]: possibleShareClasses.map((name) =>
          name.length > 30 ? `${name.substring(0, 30)}...` : name,
        ),
      }}
      sortBy={sortBy}
      viewBy={VIEW_BY}
      visibleColumns={visibleColumns}
    >
      {snapshotViewBy.all({
        defaultShareClassId: payload.defaultShareClassId,
        possibleShareClasses,
        search: '',
        setEmptySearchResult: () => undefined,
        snapshot,
        type: sortBy,
        viewBy: VIEW_BY,
        visibleColumns,
      })}
    </BasicTable>
  );
};
